/**
 * New console — navigation.
 *
 * The prototype's rail, group for group. Every screen here is a new-console screen:
 * no classic page is rendered inside this shell. The first attempt did exactly that
 * and it broke every screen, because a classic page brings its own topbar through
 * `@layout/Page` — two headers, and a layout fighting itself.
 *
 * A route whose screen is not written yet renders `Placeholder`, which is a real
 * new-console screen that says so. That keeps the console coherent while it is
 * built out, and keeps the classic UI completely out of it.
 *
 * Gating is by capability, not role name — see capabilities.js.
 */
import { lazy } from 'react';
import {
  IconHome, IconBolt, IconLeads, IconUsers, IconCalendar, IconClinic, IconCompass,
  IconTraffic, IconAds, IconBell, IconTemplates, IconShield, IconLink,
  IconCog, IconFaq, IconPlay, IconMail, IconKey, IconActivity, IconPolicy, IconTrash, IconPatient
} from './components/Icons';

const Home = lazy(() => import('./pages/Home'));
const Leads = lazy(() => import('./pages/Leads'));
const Triage = lazy(() => import('./pages/Triage'));
const Appointments = lazy(() => import('./pages/Appointments'));
const Patients = lazy(() => import('./pages/Patients'));
const PatientDetail = lazy(() => import('./pages/PatientDetail'));
const Clinics = lazy(() => import('./pages/Clinics'));
const ClinicIntegrations = lazy(() => import('./pages/Clinics').then((m) => ({ default: m.ClinicIntegrations })));
const Notifications = lazy(() => import('./pages/Notifications'));
const Users = lazy(() => import('./pages/Users'));
const Phrases = lazy(() => import('./pages/Phrases'));
const Templates = lazy(() => import('./pages/Templates'));
const ClinicForm = lazy(() => import('./pages/ClinicForm'));
const ClinicWorkspace = lazy(() => import('./pages/ClinicWorkspace'));
const Integrations = lazy(() => import('./pages/Integrations'));
const Roles = lazy(() => import('./pages/Roles'));
const Diagnose = lazy(() => import('./pages/Diagnose'));
const Traffic = lazy(() => import('./pages/Traffic'));
const TrafficDetail = lazy(() => import('./pages/TrafficDetail'));
const Ads = lazy(() => import('./pages/Ads'));
const AdsHistory = lazy(() => import('./pages/AdsHistory'));
const Settings = lazy(() => import('./pages/Settings'));
const Contact = lazy(() => import('./pages/Contact'));
const Faq = lazy(() => import('./pages/Help'));
const Tutorial = lazy(() => import('./pages/Help').then((m) => ({ default: m.Tutorial })));
const Profile = lazy(() => import('./pages/Profile'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const ChangeEmail = lazy(() => import('./pages/ChangeEmail'));
const LeadDetail = lazy(() => import('./pages/LeadDetail'));
const DeletedLeads = lazy(() => import('./pages/DeletedLeads'));
const Privacy = lazy(() => import('./pages/Privacy'));
const NotificationConfirmed = lazy(() => import('./pages/NotificationConfirmed'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));
const Placeholder = lazy(() => import('./pages/Placeholder'));

/* Every route here is built and on a live endpoint. Two screens the prototype sketches
   are deliberately absent rather than shown as stubs: automations and the audit log have
   no endpoint behind them, so each would be a screen that reads nothing and saves
   nowhere. Placeholder is kept as the fallback for anything added to the rail before its
   screen exists.

   `key` is the screen's letter in the keyboard chord — "g" then the letter opens it
   from anywhere (the prototype's G-chords). The shell binds them and the shortcuts
   list prints them from this same table, so a key cannot be listed and not work. */
export const navGroups = [
  {
    group: 'Work',
    items: [
      { id: '/home',         label: 'Home',          icon: IconHome,      cap: 'leads.view', key: 'h' },
      { id: '/triage',       label: 'Triage queue',  icon: IconBolt,      cap: 'leads.triage', key: 't' },
      { id: '/leads',        label: 'Leads',         icon: IconLeads,     cap: 'leads.view', key: 'l' },
      { id: '/appointments', label: 'Appointments',  icon: IconCalendar,  cap: 'leads.view', key: 'a' },
      /* One record per person across all their leads — the read-across the lead list
         cannot give. Grouped by the API's procedure, never in the browser. */
      { id: '/patients',     label: 'Patients',      icon: IconPatient,   cap: 'patients.view', key: 'p' }
    ]
  },
  {
    group: 'My clinic',
    items: [
      { id: '/templates', label: 'Templates', icon: IconTemplates, cap: 'templates.view' },
      /* The classic's Notification Settings: who at this clinic is emailed about leads.
         Clinic admin only, as the endpoints are. */
      { id: '/notification-settings', label: 'Notifications', icon: IconBell, cap: 'notify.recipients' },
      /* A portfolio login's clinic entry is the clinic list, each clinic's workspace one
         click on, while a clinic admin's is their own clinic. `hideFor` keeps the second
         out of the rail for a role that has the first; its route stays open to both. */
      { id: '/clinics',   label: 'Clinics',   icon: IconClinic,    cap: 'clinics.view', key: 'c' },
      { id: '/clinic',    label: 'Clinic',    icon: IconClinic,    cap: 'clinic.settings', hideFor: 'clinics.view' }
    ]
  },
  {
    group: 'Portfolio',
    items: [
      /* Every clinic against every provider. A menu of its own, apart from API keys,
         which shows and edits the keys of the one clinic in scope. */
      { id: '/integrations-overview', label: 'Integrations', icon: IconLink, cap: 'clinics.view' },
      { id: '/diagnose',      label: 'Diagnose',    icon: IconCompass,  cap: 'clinics.view', key: 'd' },
      { id: '/traffic-drop',  label: 'Traffic',     icon: IconTraffic,  cap: 'growth.view' },
      { id: '/ads',           label: 'Google Ads',  icon: IconAds,      cap: 'growth.view' },
      { id: '/notifications', label: 'Notifications', icon: IconBell,   cap: 'notify.log', key: 'n' }
    ]
  },
  {
    group: 'Team',
    items: [
      { id: '/users', label: 'Users', icon: IconUsers,  cap: 'users.view', key: 'u' },
      /* Hidden from the rail on request; the screen and its route are kept, so a direct
         link still opens it and bringing it back is removing `hidden`. */
      { id: '/roles', label: 'Roles', icon: IconShield, cap: 'roles.manage', hidden: true }
    ]
  },
  {
    group: 'System',
    items: [
      /* One clinic's credentials, which is what 'API keys' describes and what the
         prototype calls it. Every clinic's integrations side by side are Integrations,
         under Portfolio. */
      { id: '/integrations', label: 'API keys',       icon: IconKey,       cap: 'integrations.view' },
      { id: '/phrases',      label: 'Script phrases', icon: IconTemplates, cap: 'settings.manage' }
    ]
  },
  {
    group: 'Account',
    items: [
      { id: '/settings', label: 'Settings',        icon: IconCog,  cap: null, key: 's' },
      { id: '/faq',      label: 'Help & FAQ',      icon: IconFaq,  cap: null },
      { id: '/tutorial', label: 'Getting started', icon: IconPlay, cap: null },
      { id: '/contact',  label: 'Contact',         icon: IconMail, cap: null },
      /* The classic carries this in the account menu and the footer, and serves it
         signed out as well. Neither exists here, so it takes a rail slot beside
         Contact — a policy nobody can reach is not published. */
      { id: '/privacy-policy', label: 'Privacy policy', icon: IconPolicy, cap: null }
    ]
  }
];

/* Screens reached from another screen rather than from the rail. Each names the screen
   it is opened from in `back`, which the topbar's back link and the breadcrumb follow.
   A screen opened from somewhere else for one visit says so in navigation state — see
   routeBack. */
const subRoutes = [
  { id: '/ads-history', label: 'Keyword history', icon: IconActivity, cap: 'growth.view', group: 'Portfolio', back: '/ads' },
  { id: '/leads/:leadId', label: 'Lead detail', icon: IconLeads, cap: 'leads.view', group: 'Work', back: '/leads' },
  { id: '/patients/:patientKey', label: 'Patient record', icon: IconPatient, cap: 'patients.view', group: 'Work', back: '/patients' },
  /* The archive of deleted leads: from the account menu alone, not the rail or the
     list — a review screen, not daily work. */
  { id: '/deleted-leads', label: 'Deleted leads', icon: IconTrash, cap: 'leads.delete', group: 'Work', back: '/leads' },
  { id: '/traffic-drop/:clinicId', label: 'Clinic traffic', icon: IconTraffic, cap: 'growth.view', group: 'Portfolio', back: '/traffic-drop' },
  { id: '/clinic/:clinicId', label: 'Clinic', icon: IconClinic, cap: 'clinic.settings', group: 'My clinic', back: '/clinics' },
  { id: '/clinic-form', label: 'Clinic details', icon: IconClinic, cap: 'clinic.settings', group: 'My clinic', back: '/clinic' },
  /* One clinic's API keys. The menu's /integrations is every clinic's. */
  { id: '/integrations/:clinicId', label: 'API keys', icon: IconKey, cap: 'integrations.view', group: 'System', back: '/integrations' },
  /* Reached from the account panel rather than the rail: they are about the person,
     not the work, and a rail slot each would push the actual screens down. `hidden`
     keeps them out of the command palette's page list for the same reason. */
  { id: '/profile',         label: 'Your profile',    icon: IconUsers, cap: null, group: 'Account' },
  /* Where an alert recipient's confirmation email lands when they happen to be signed in
     here (a signed-out visitor gets the classic's public page). Not a destination anyone
     navigates to, so kept out of the palette. */
  { id: '/notification-confirmed', label: 'Email confirmed', icon: IconMail, cap: null, group: 'Account', hidden: true },
  { id: '/change-password', label: 'Change password', icon: IconKey,   cap: null, group: 'Account', back: '/profile' },
  { id: '/change-email',    label: 'Change email',    icon: IconMail,  cap: null, group: 'Account', back: '/profile' }
];

export const navRoutes = navGroups.reduce((all, g) => all.concat(g.items), []).concat(subRoutes);

/** The chord routes by letter, in rail order. */
export const CHORDS = navRoutes.filter((r) => r.key).reduce((m, r) => Object.assign(m, { [r.key]: r }), {});

const SCREENS = {
  '/home': Home,
  '/leads': Leads,
  '/triage': Triage,
  '/appointments': Appointments,
  '/patients': Patients,
  '/patients/:patientKey': PatientDetail,
  '/clinics': Clinics,
  '/integrations-overview': ClinicIntegrations,
  '/notifications': Notifications,
  '/users': Users,
  '/phrases': Phrases,
  '/templates': Templates,
  '/clinic': ClinicWorkspace,
  '/clinic/:clinicId': ClinicWorkspace,
  '/clinic-form': ClinicForm,
  '/integrations': Integrations,
  '/integrations/:clinicId': Integrations,
  '/roles': Roles,
  '/diagnose': Diagnose,
  '/traffic-drop': Traffic,
  '/traffic-drop/:clinicId': TrafficDetail,
  '/ads': Ads,
  '/settings': Settings,
  '/faq': Faq,
  '/tutorial': Tutorial,
  '/contact': Contact,
  '/privacy-policy': Privacy,
  '/profile': Profile,
  '/change-password': ChangePassword,
  '/change-email': ChangeEmail,
  '/notification-confirmed': NotificationConfirmed,
  '/notification-settings': NotificationSettings,
  '/leads/:leadId': LeadDetail,
  '/deleted-leads': DeletedLeads,
  '/ads-history': AdsHistory
};

export const screenFor = (item) => {
  const Screen = SCREENS[item.id];
  return Screen ? <Screen /> : <Placeholder title={item.label} />;
};

/* Routes carrying a parameter — /leads/:leadId — never equal the pathname they
   match, so the topbar looked up an empty title and rendered a headerless page.
   One matcher, used by all three helpers. */
const matches = (id, pathname) => {
  if (id === pathname) return true;
  if (id.indexOf(':') < 0) return false;
  const a = id.split('/');
  const b = pathname.split('/');
  return a.length === b.length && a.every((seg, i) => seg.startsWith(':') || seg === b[i]);
};

const routeFor = (pathname) => navRoutes.find((r) => matches(r.id, pathname));

export const routeTitle = (pathname) => {
  const hit = routeFor(pathname);
  return hit ? hit.label : '';
};

const routeGroup = (pathname) => {
  const sub = subRoutes.find((r) => matches(r.id, pathname));
  if (sub) return sub.group;
  const g = navGroups.find((x) => x.items.some((i) => i.id === pathname));
  return g ? g.group : '';
};

/* Where the back link in the topbar points for a screen opened from another one.
   Returning nothing means the screen stands on its own and gets a plain crumb.

   `state.back` is a screen saying exactly where it was opened from — the clinic form
   from the clinic list when creating, from a clinic's workspace when editing — for a
   route whose usual parent is not the right one this time. `state.from` is the older
   tag form of the same idea. Both hold for one visit and never outlive it. */
export const routeBack = (pathname, state) => {
  if (state && state.back) return state.back;
  if (matches('/leads/:leadId', pathname) && state && state.from === 'triage') return '/triage';
  if (matches('/clinic/:clinicId', pathname) && state && state.from === 'diagnose') return '/diagnose';
  const hit = routeFor(pathname);
  return hit && hit.back ? hit.back : '';
};

/* The value a parameterised route carries at `name` — the 5 in /clinic/5. */
const paramOf = (id, pathname, name) => {
  const a = id.split('/');
  const b = pathname.split('/');
  const i = a.indexOf(`:${name}`);
  return i >= 0 ? b[i] || '' : '';
};

/**
 * The breadcrumb: every screen above this one, in order, ending on this one.
 *
 * Built by following `back` from the current screen until a screen that stands on its
 * own, so the trail is the same chain the back link already walks — one hop at a time
 * — and cannot disagree with it. `state.from` (a lead opened from the triage queue, a
 * clinic opened from Diagnose) bends the first hop the way routeBack does; it says
 * nothing about the hops above that.
 *
 * A screen about one clinic is named after the clinic when `nameFor` can say which:
 * "Portfolio › Traffic › Bellevue Chiropractic" is the trail somebody would say out
 * loud, and "Traffic › Clinic traffic" is not. The rail group goes first as context,
 * not as a link — it is a heading in the rail, not a screen — and Home, which is its
 * own group's first screen, gets no group above it.
 */
export const routeTrail = (pathname, state, nameFor) => {
  const trail = [];
  const seen = {};
  let path = pathname;
  let st = state;
  while (path && !seen[path]) {
    seen[path] = true;
    const hit = routeFor(path);
    if (!hit) break;
    const clinicId = Number(paramOf(hit.id, path, 'clinicId')) || 0;
    const name = clinicId && nameFor ? nameFor(clinicId) : '';
    trail.unshift({ to: path, label: name || hit.label });
    path = routeBack(path, st);
    st = null;
  }
  const group = pathname === '/home' ? '' : routeGroup(pathname);
  if (group && trail.length) trail.unshift({ label: group });
  return trail;
};

